// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xBaseTheme } from "@stoneage/XDL/types";
import { color, styledScrollbar } from "@stoneage/XDL/Utilities";
import { TabProps } from "@insite/mobius/Tab";
import { css } from "styled-components";
import { fontConfig } from "@stoneage/XDL/constants";
import { TypographyPresentationProps } from "@insite/mobius/Typography";

const tabLabelStyle: TypographyPresentationProps = {
    ...fontConfig.regularSingleLine,
    weight: fontConfig.weight.medium,
    color: "secondary",
    css: css`
        margin-bottom: 0;
    `,
};

export default {
    defaultProps: {
        mergeCss: true,
        typographyProps: {
            ...tabLabelStyle,
        },
        css: css`
            position: relative;
            padding: 0 0 6px 0;
            cursor: pointer;
            border-bottom: 0;
            min-width: initial;
            font-size: ${tabLabelStyle.size}px;
            line-height: ${tabLabelStyle.lineHeight};
            font-weight: ${tabLabelStyle.weight};
            color: ${color(tabLabelStyle.color)};

            &::after {
                content: "";
                position: absolute;
                bottom: 0;
                left: 0;
                width: 0;
                height: 4px;
                background-color: ${color("primary")};
                transition: width 0.3s ease;
            }

            &:hover {
                border-bottom: 0;
                &::after {
                    width: 100%;
                }
            }

            &:focus {
                outline: none;
            }

            ${({ selected }: TabProps) =>
                selected &&
                css`
                    &::after {
                        width: 100%;
                    }
                `}
        `,
    },
    groupDefaultProps: {
        mergeCss: true,
        cssOverrides: {
            tabGroup: css`
                padding: 0;
                position: relative;
                gap: 32px;

                &:after {
                    content: "";
                    position: absolute;
                    display: block;
                    background-color: ${color("common.border")};
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 1px;
                    width: 100%;
                }

                ${styledScrollbar(4)}
            `,
            tabContent: css`
                position: relative;
                margin-top: 0;
                padding: 16px 0;
                border-top: 0;
                border-bottom: 0;
            `,
            wrapper: css`
                width: 100%;
            `,
        },
    },
} as xBaseTheme["tab"];
